import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Eye,
  Info,
  ListChecks,
  Loader2,
  MapPin,
  Package,
  RefreshCw,
  Search,
  Truck,
  X,
  XCircle,
} from 'lucide-react';

import api from '../../../../services/api';
import { getApiErrorMessage } from '../../../../utils/apiError';
import Layout from '../components/layout/Layout.tsx';
import {
  ORDERS_REFRESH_INTERVAL_MS,
} from '../constants/timing.js';

interface SubItem {
  descricao?: string;
  nome?: string;
}

interface Item {
  quantidade?: number;
  nome?: string;
  subItens?: SubItem[];
  observacao?: string;
}

interface Pessoa {
  nome?: string;
  telefone?: string;
  endereco?: string;
  numero?: string | number;
  complemento?: string;
  bairro?: string;
}

interface PedidoData {
  id?: string;
  idUnicoIntegracao?: string;
  pessoa?: Pessoa;
  tipo_pagamento?: string;
  tipoDelivery?: string;
  valorTotal?: number | string;
  valor_total?: number | string;
  valorTaxaEntrega?: number | string;
  desconto?: number | string;
  troco?: number | string;
  descricao?: string;
  itens?: Item[];
  cancelamento_motivo?: string;
}

interface Pedido {
  id: string;
  status: string;
  criado_em: string;
  cliente_id?: string | number;
  pedido?: PedidoData;
  atendabot_clientes?: {
    nome?: string;
    telefone?: string;
  };
}

type KanbanStatus =
  | 'iniciado'
  | 'em_preparo'
  | 'em_transporte'
  | 'finalizado'
  | 'cancelado';

type OpenKanbanStatus = Exclude<KanbanStatus, 'cancelado'>;

type PedidoWithStatus = Pedido & {
  kanbanStatus: KanbanStatus;
};

type ColumnPrefs = {
  em_preparo: boolean;
  em_transporte: boolean;
};

type FetchPedidosOptions = {
  silent?: boolean;
  keepDataOnError?: boolean;
  suppressError?: boolean;
  trackRefreshing?: boolean;
};

const STATUS_FLOW: OpenKanbanStatus[] = [
  'iniciado',
  'em_preparo',
  'em_transporte',
  'finalizado',
];

const DEFAULT_COLUMN_PREFS: ColumnPrefs = {
  em_preparo: true,
  em_transporte: true,
};

const KANBAN_COLUMNS_STORAGE_KEY = 'atendabot-orders-kanban-columns-v1';
const CANCEL_REASON_SUGGESTIONS = [
  'Delivery indisponível',
  'Ingredientes insuficientes para concluir o pedido',
];

const RAW_STATUS_TO_KANBAN: Record<string, KanbanStatus> = {
  iniciado: 'iniciado',
  created: 'iniciado',
  waiting: 'iniciado',
  processing: 'iniciado',
  em_preparo: 'em_preparo',
  confirmed: 'em_preparo',
  confirmado: 'em_preparo',
  ready_to_deliver: 'em_preparo',
  em_transporte: 'em_transporte',
  dispatched: 'em_transporte',
  finalizado: 'finalizado',
  done: 'finalizado',
  concluded: 'finalizado',
  cancelado: 'cancelado',
  canceled: 'cancelado',
  cancelled: 'cancelado',
  failed: 'cancelado',
};

const STATUS_LABELS: Record<KanbanStatus, string> = {
  iniciado: 'Iniciado',
  em_preparo: 'Em preparo',
  em_transporte: 'Em transporte',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

const STATUS_BADGE_CLASSES: Record<KanbanStatus, string> = {
  iniciado: 'bg-amber-50 text-amber-700 border border-amber-200',
  em_preparo: 'bg-blue-50 text-blue-700 border border-blue-200',
  em_transporte: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  finalizado: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancelado: 'bg-rose-50 text-rose-700 border border-rose-200',
};

const COLUMN_STYLES: Record<OpenKanbanStatus, { border: string; iconClass: string }> = {
  iniciado: {
    border: 'border-amber-300',
    iconClass: 'text-amber-500',
  },
  em_preparo: {
    border: 'border-blue-300',
    iconClass: 'text-blue-500',
  },
  em_transporte: {
    border: 'border-indigo-300',
    iconClass: 'text-indigo-500',
  },
  finalizado: {
    border: 'border-emerald-300',
    iconClass: 'text-emerald-500',
  },
};

const API_STATUS_VALUE: Record<KanbanStatus, KanbanStatus> = {
  iniciado: 'iniciado',
  em_preparo: 'em_preparo',
  em_transporte: 'em_transporte',
  finalizado: 'finalizado',
  cancelado: 'cancelado',
};

function hasActiveIntegracao(config: any) {
  return config?.integration_saipos_active === true || config?.integration_sischef_active === true;
}

function normalizeStatus(status: string): KanbanStatus {
  const normalized = String(status || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

  return RAW_STATUS_TO_KANBAN[normalized] || 'iniciado';
}

function getVisibleStatus(status: KanbanStatus, prefs: ColumnPrefs): OpenKanbanStatus | 'cancelado' {
  if (status === 'cancelado') return 'cancelado';
  if (status === 'iniciado' || status === 'finalizado') return status;

  if (status === 'em_preparo') {
    return prefs.em_preparo ? 'em_preparo' : 'iniciado';
  }

  if (prefs.em_transporte) return 'em_transporte';
  if (prefs.em_preparo) return 'em_preparo';
  return 'iniciado';
}

function loadColumnPrefs(): ColumnPrefs {
  if (typeof window === 'undefined') return DEFAULT_COLUMN_PREFS;

  try {
    const raw = window.localStorage.getItem(KANBAN_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMN_PREFS;

    const parsed = JSON.parse(raw);
    return {
      em_preparo:
        typeof parsed?.em_preparo === 'boolean'
          ? parsed.em_preparo
          : DEFAULT_COLUMN_PREFS.em_preparo,
      em_transporte:
        typeof parsed?.em_transporte === 'boolean'
          ? parsed.em_transporte
          : DEFAULT_COLUMN_PREFS.em_transporte,
    };
  } catch (_error) {
    return DEFAULT_COLUMN_PREFS;
  }
}

function getTodayDateStr() {
  const today = new Date();
  return today.toISOString().slice(0, 10);
}

function formatDate(dateValue: string | Date) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const sanitized = value.trim().replace(/\./g, '').replace(',', '.');
  if (!sanitized) return null;

  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCurrency(value: unknown) {
  const numeric = toNumber(value);
  if (numeric === null) return null;

  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function getPedidoItems(pedido: Pedido): Item[] {
  return Array.isArray(pedido?.pedido?.itens) ? pedido.pedido!.itens! : [];
}

function getPedidoDisplayId(pedido: Pedido) {
  return String(pedido?.pedido?.idUnicoIntegracao || pedido?.id || '').trim();
}

function getClienteNome(pedido: Pedido) {
  return (
    pedido?.pedido?.pessoa?.nome
    || pedido?.atendabot_clientes?.nome
    || 'Cliente'
  );
}

function getClienteTelefone(pedido: Pedido) {
  return (
    pedido?.pedido?.pessoa?.telefone
    || pedido?.atendabot_clientes?.telefone
    || ''
  );
}

function formatEndereco(pessoa?: Pessoa) {
  if (!pessoa) return '';

  const parts = [pessoa.endereco, pessoa.numero].filter(Boolean);
  let address = parts.join(', ');

  if (pessoa.bairro) {
    address = address ? `${address} - ${pessoa.bairro}` : pessoa.bairro;
  }
  if (pessoa.complemento) {
    address = address ? `${address} (${pessoa.complemento})` : pessoa.complemento;
  }

  return address;
}

function getTipoEntregaLabel(tipoDelivery?: string) {
  const normalized = String(tipoDelivery || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

  if (['RETIRAR', 'RETIRADA', 'PICKUP', 'TAKEOUT', 'BALCAO', 'RETIRADA_BALCAO'].includes(normalized)) {
    return 'Retirada';
  }
  if (!normalized) return 'Entrega';
  return 'Delivery';
}

function isRetiradaPedido(pedido: Pedido | PedidoWithStatus) {
  const normalized = String(pedido?.pedido?.tipoDelivery || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

  return ['RETIRAR', 'RETIRADA', 'PICKUP', 'TAKEOUT', 'BALCAO', 'RETIRADA_BALCAO'].includes(normalized);
}

function getStatusIndex(status: KanbanStatus) {
  if (status === 'cancelado') return -1;
  return STATUS_FLOW.indexOf(status as OpenKanbanStatus);
}

function canAdvanceStatus(
  currentStatus: KanbanStatus,
  targetStatus: OpenKanbanStatus,
  options: { isRetirada?: boolean } = {}
) {
  if (options.isRetirada && targetStatus === 'em_transporte') {
    return false;
  }

  const currentIndex = getStatusIndex(currentStatus);
  const targetIndex = STATUS_FLOW.indexOf(targetStatus);

  if (currentIndex < 0 || targetIndex < 0) return false;
  return targetIndex > currentIndex;
}

function getStatusOptionsForPedido(
  currentStatus: KanbanStatus,
  prefs: ColumnPrefs,
  flowOptions: { isRetirada?: boolean } = {}
): KanbanStatus[] {
  if (currentStatus === 'cancelado') return ['cancelado'];

  const options: KanbanStatus[] = [currentStatus];
  const currentIndex = getStatusIndex(currentStatus);

  STATUS_FLOW.forEach((status, index) => {
    if (index <= currentIndex) return;
    if (status === 'em_preparo' && !prefs.em_preparo) return;
    if (status === 'em_transporte' && !prefs.em_transporte) return;
    if (status === 'em_transporte' && flowOptions.isRetirada) return;
    options.push(status);
  });

  options.push('cancelado');
  return Array.from(new Set(options));
}

function renderColumnIcon(status: OpenKanbanStatus, className: string) {
  if (status === 'iniciado') return <Package size={14} className={className} />;
  if (status === 'em_preparo') return <RefreshCw size={14} className={className} />;
  if (status === 'em_transporte') return <Truck size={14} className={className} />;
  return <CheckCircle2 size={14} className={className} />;
}

function PedidoDetailsContent({ pedido }: { pedido: PedidoWithStatus }) {
  const itens = getPedidoItems(pedido);
  const endereco = formatEndereco(pedido?.pedido?.pessoa);
  const tipoEntrega = getTipoEntregaLabel(pedido?.pedido?.tipoDelivery);
  const payment = pedido?.pedido?.tipo_pagamento;
  const totalFormatado = formatCurrency(
    pedido?.pedido?.valorTotal ?? pedido?.pedido?.valor_total
  );
  const taxaEntrega = formatCurrency(pedido?.pedido?.valorTaxaEntrega);
  const desconto = formatCurrency(pedido?.pedido?.desconto);
  const troco = formatCurrency(pedido?.pedido?.troco);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
        <div className="min-w-0">
          <p className="text-base font-bold text-gray-900 truncate">{getClienteNome(pedido)}</p>
          {getClienteTelefone(pedido) && (
            <p className="text-sm text-gray-500 truncate">{getClienteTelefone(pedido)}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">{formatDate(pedido.criado_em)}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE_CLASSES[pedido.kanbanStatus]}`}>
          {STATUS_LABELS[pedido.kanbanStatus]}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
          {tipoEntrega}
        </span>
        {payment && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            <CreditCard size={12} />
            {payment}
          </span>
        )}
      </div>

      {endereco && (
        <div className="flex items-start gap-2 text-sm text-gray-700 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
          <MapPin size={14} className="mt-0.5 text-orange-500 shrink-0" />
          <span>{endereco}</span>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Itens completos</p>
        {itens.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-500">
            Nenhum item encontrado para este pedido.
          </div>
        ) : (
          itens.map((item, index) => (
            <div key={`${pedido.id}-detail-${index}`} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-sm text-gray-800">
                <span className="font-semibold">{item.quantidade || 1}x</span>{' '}
                {item.nome || 'Item sem nome'}
              </p>
              {Array.isArray(item.subItens) && item.subItens.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Complementos: {item.subItens.map((sub) => sub.descricao || sub.nome).filter(Boolean).join(', ')}
                </p>
              )}
              {item.observacao && (
                <p className="text-xs text-blue-700 mt-1">Observação: {item.observacao}</p>
              )}
            </div>
          ))
        )}
      </div>

      {pedido?.pedido?.descricao && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Observações gerais</p>
          <p className="text-sm text-blue-900 mt-1">{pedido.pedido.descricao}</p>
        </div>
      )}

      <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 space-y-1.5">
        {desconto && (
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>Desconto</span>
            <span>- {desconto}</span>
          </div>
        )}
        {taxaEntrega && (
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>Taxa de entrega</span>
            <span>{taxaEntrega}</span>
          </div>
        )}
        {troco && (
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>Troco para</span>
            <span>{troco}</span>
          </div>
        )}
        {totalFormatado && (
          <div className="flex items-center justify-between text-base font-semibold text-gray-900 pt-1 border-t border-gray-200">
            <span>Total</span>
            <span>{totalFormatado}</span>
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-400">ID do pedido: {getPedidoDisplayId(pedido) || pedido.id}</p>
    </div>
  );
}

const Orders = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [dateStart, setDateStart] = useState(getTodayDateStr());
  const [dateEnd, setDateEnd] = useState(getTodayDateStr());
  const [columnPrefs, setColumnPrefs] = useState<ColumnPrefs>(loadColumnPrefs);
  const [updatingPedidoId, setUpdatingPedidoId] = useState<string | null>(null);
  const [integrationActive, setIntegrationActive] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [showCancelados, setShowCancelados] = useState(false);
  const [detailPedidoId, setDetailPedidoId] = useState<string | null>(null);
  const [cancelDialog, setCancelDialog] = useState<{
    pedidoId: string | null;
    motivo: string;
    error: string;
    submitting: boolean;
    confirming: boolean;
  }>({
    pedidoId: null,
    motivo: '',
    error: '',
    submitting: false,
    confirming: false,
  });
  const requestInFlightRef = useRef(false);
  const updatingPedidoIdRef = useRef<string | null>(null);
  const cancelSubmittingRef = useRef(false);
  const pendingRealtimeRefreshRef = useRef(false);

  const fetchPedidos = useCallback(async ({
    silent = false,
    keepDataOnError = false,
    suppressError = false,
    trackRefreshing = true,
  }: FetchPedidosOptions = {}) => {
    if (requestInFlightRef.current) {
      pendingRealtimeRefreshRef.current = true;
      return false;
    }
    requestInFlightRef.current = true;

    if (silent && trackRefreshing) {
      setIsRefreshing(true);
    } else if (!silent) {
      setIsLoading(true);
    }

    if (!suppressError) {
      setFeedbackError('');
    }

    try {
      const { data } = await api.get('/atendabot/pedidos', {
        params: { dateStart, dateEnd },
      });
      setPedidos(Array.isArray(data) ? data : []);
      return true;
    } catch (error: any) {
      if (!keepDataOnError) {
        setPedidos([]);
      }
      if (!suppressError) {
        setFeedbackError(getApiErrorMessage(error, 'Erro ao carregar pedidos.'));
      }
      return false;
    } finally {
      requestInFlightRef.current = false;
      if (silent && trackRefreshing) {
        setIsRefreshing(false);
      } else if (!silent) {
        setIsLoading(false);
      }

      if (
        pendingRealtimeRefreshRef.current
        && typeof window !== 'undefined'
        && typeof document !== 'undefined'
        && document.visibilityState === 'visible'
        && updatingPedidoIdRef.current === null
        && !cancelSubmittingRef.current
      ) {
        pendingRealtimeRefreshRef.current = false;
        window.setTimeout(() => {
          void fetchPedidos({
            silent: true,
            keepDataOnError: true,
            suppressError: true,
            trackRefreshing: false,
          });
        }, 250);
      }
    }
  }, [dateStart, dateEnd]);

  useEffect(() => {
    let disposed = false;

    const fetchIntegrationConfig = async () => {
      try {
        const { data } = await api.get('/atendabot/config');
        if (disposed) return;
        setIntegrationActive(hasActiveIntegracao(data));
      } catch (_error) {
        if (disposed) return;
        setIntegrationActive(false);
      }
    };

    void fetchIntegrationConfig();

    return () => {
      disposed = true;
    };
  }, []);

  useEffect(() => {
    void fetchPedidos();
  }, [fetchPedidos]);

  useEffect(() => {
    updatingPedidoIdRef.current = updatingPedidoId;
  }, [updatingPedidoId]);

  useEffect(() => {
    cancelSubmittingRef.current = cancelDialog.submitting;
  }, [cancelDialog.submitting]);

  useEffect(() => {
    if (updatingPedidoId !== null || cancelDialog.submitting) return;
    if (!pendingRealtimeRefreshRef.current) return;
    pendingRealtimeRefreshRef.current = false;
    void fetchPedidos({
      silent: true,
      keepDataOnError: true,
      suppressError: true,
      trackRefreshing: false,
    });
  }, [updatingPedidoId, cancelDialog.submitting, fetchPedidos]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const refreshSilently = () => {
      void fetchPedidos({
        silent: true,
        keepDataOnError: true,
        suppressError: true,
        trackRefreshing: false,
      });
    };

    const flushPendingIfNeeded = () => {
      if (document.visibilityState !== 'visible') return;
      if (updatingPedidoIdRef.current !== null || cancelSubmittingRef.current) return;
      if (!pendingRealtimeRefreshRef.current) return;
      pendingRealtimeRefreshRef.current = false;
      refreshSilently();
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        pendingRealtimeRefreshRef.current = true;
        return;
      }

      if (updatingPedidoIdRef.current !== null || cancelSubmittingRef.current) {
        pendingRealtimeRefreshRef.current = true;
        return;
      }

      refreshSilently();
    }, ORDERS_REFRESH_INTERVAL_MS);

    window.addEventListener('focus', flushPendingIfNeeded);
    document.addEventListener('visibilitychange', flushPendingIfNeeded);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', flushPendingIfNeeded);
      document.removeEventListener('visibilitychange', flushPendingIfNeeded);
    };
  }, [fetchPedidos]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      KANBAN_COLUMNS_STORAGE_KEY,
      JSON.stringify(columnPrefs)
    );
  }, [columnPrefs]);

  const pedidosNormalizadosAll = useMemo<PedidoWithStatus[]>(() => (
    pedidos.map((pedido) => ({
      ...pedido,
      kanbanStatus: normalizeStatus(pedido.status),
    }))
  ), [pedidos]);

  const pedidosFiltrados = useMemo<PedidoWithStatus[]>(() => {
    const termo = search.trim().toLowerCase();
    if (!termo) return pedidosNormalizadosAll;

    const termoDigitos = termo.replace(/\D/g, '');

    return pedidosNormalizadosAll.filter((pedido) => {
      const nome = getClienteNome(pedido).toLowerCase();
      const telefone = getClienteTelefone(pedido);
      const telefoneDigitos = String(telefone || '').replace(/\D/g, '');
      const pedidoId = getPedidoDisplayId(pedido).toLowerCase();
      const itensTexto = getPedidoItems(pedido)
        .map((item) => `${item?.nome || ''} ${item?.observacao || ''}`)
        .join(' ')
        .toLowerCase();

      if (nome.includes(termo) || pedidoId.includes(termo) || itensTexto.includes(termo)) {
        return true;
      }

      if (termoDigitos && telefoneDigitos.includes(termoDigitos)) {
        return true;
      }

      return false;
    });
  }, [pedidosNormalizadosAll, search]);

  const groupedPedidos = useMemo(() => {
    const open: Record<OpenKanbanStatus, PedidoWithStatus[]> = {
      iniciado: [],
      em_preparo: [],
      em_transporte: [],
      finalizado: [],
    };

    const cancelados: PedidoWithStatus[] = [];

    pedidosFiltrados.forEach((pedido) => {
      const visibleStatus = getVisibleStatus(pedido.kanbanStatus, columnPrefs);

      if (visibleStatus === 'cancelado') {
        cancelados.push(pedido);
        return;
      }

      open[visibleStatus].push(pedido);
    });

    return { open, cancelados };
  }, [pedidosFiltrados, columnPrefs]);

  const visibleColumns = useMemo<OpenKanbanStatus[]>(() => {
    return STATUS_FLOW.filter((status) => {
      if (status === 'iniciado' || status === 'finalizado') return true;
      if (status === 'em_preparo') return columnPrefs.em_preparo;
      return columnPrefs.em_transporte;
    });
  }, [columnPrefs]);

  const detailPedido = useMemo(() => (
    pedidosNormalizadosAll.find((item) => String(item.id) === detailPedidoId) || null
  ), [pedidosNormalizadosAll, detailPedidoId]);

  const handleFilterSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fetchPedidos();
  };

  const openCancelDialog = (pedidoId: string) => {
    setCancelDialog({
      pedidoId,
      motivo: '',
      error: '',
      submitting: false,
      confirming: false,
    });
  };

  const updatePedidoStatus = async (
    pedido: PedidoWithStatus,
    targetStatus: KanbanStatus,
    motivoCancelamento?: string
  ) => {
    setUpdatingPedidoId(String(pedido.id));
    setFeedbackError('');

    try {
      const { data } = await api.put(`/atendabot/pedidos/${pedido.id}/status`, {
        status: API_STATUS_VALUE[targetStatus],
        ...(motivoCancelamento ? { motivoCancelamento } : {}),
      });
      await fetchPedidos({ silent: true });
      if (data?.notificacao?.attempted && data.notificacao.sent === false) {
        setFeedbackError('O pedido foi atualizado, mas não foi possível enviar a mensagem ao cliente. Confira a conexão do WhatsApp.');
      }
    } catch (error: any) {
      setFeedbackError(getApiErrorMessage(error, 'Erro ao atualizar status do pedido.'));
      throw error;
    } finally {
      setUpdatingPedidoId(null);
    }
  };
  const handleStatusChange = async (pedido: PedidoWithStatus, nextValue: string) => {
    if (integrationActive) return;

    const nextStatus = normalizeStatus(nextValue);
    if (!nextStatus || nextStatus === pedido.kanbanStatus) return;
    const isRetirada = isRetiradaPedido(pedido);

    if (nextStatus === 'cancelado') {
      openCancelDialog(String(pedido.id));
      return;
    }

    if (isRetirada && nextStatus === 'em_transporte') {
      setFeedbackError('Pedidos de retirada não podem ir para "Em transporte".');
      return;
    }

    if (!canAdvanceStatus(pedido.kanbanStatus, nextStatus as OpenKanbanStatus, { isRetirada })) {
      setFeedbackError('Só é permitido avançar o pedido para a próxima etapa do fluxo.');
      return;
    }

    await updatePedidoStatus(pedido, nextStatus);
  };

  const closeCancelDialog = () => {
    if (cancelDialog.submitting) return;
    setCancelDialog({
      pedidoId: null,
      motivo: '',
      error: '',
      submitting: false,
      confirming: false,
    });
  };

  const submitCancelamento = async () => {
    if (!cancelDialog.pedidoId) return;

    const motivo = cancelDialog.motivo.trim();
    if (!motivo) {
      setCancelDialog((current) => ({
        ...current,
        error: 'Informe o motivo do cancelamento para notificar o cliente.',
      }));
      return;
    }

    if (!cancelDialog.confirming) {
      setCancelDialog((current) => ({
        ...current,
        confirming: true,
        error: '',
      }));
      return;
    }

    const pedido = pedidosNormalizadosAll.find((item) => String(item.id) === cancelDialog.pedidoId);
    if (!pedido) {
      closeCancelDialog();
      return;
    }

    setCancelDialog((current) => ({ ...current, submitting: true, error: '' }));

    try {
      await updatePedidoStatus(pedido, 'cancelado', motivo);
      closeCancelDialog();
    } catch (_error) {
      setCancelDialog((current) => ({
        ...current,
        submitting: false,
        confirming: false,
        error: 'Não foi possível cancelar o pedido agora.',
      }));
    }
  };

  const handleDragEnd = (result: DropResult) => {
    void (async () => {
      if (integrationActive) return;

      const { destination, source, draggableId } = result;

      if (!destination) return;
      if (source.droppableId === destination.droppableId) return;

      const targetStatus = destination.droppableId as OpenKanbanStatus;
      if (!STATUS_FLOW.includes(targetStatus)) return;

      const pedidoId = String(draggableId || '').replace(/^pedido-/, '');
      const pedido = pedidosFiltrados.find((item) => String(item.id) === pedidoId);
      if (!pedido) return;
      const isRetirada = isRetiradaPedido(pedido);

      if (isRetirada && targetStatus === 'em_transporte') {
        setFeedbackError('Pedidos de retirada não podem ir para "Em transporte".');
        return;
      }

      if (!canAdvanceStatus(pedido.kanbanStatus, targetStatus, { isRetirada })) {
        setFeedbackError('Só é permitido mover pedidos para frente no fluxo.');
        return;
      }

      await updatePedidoStatus(pedido, targetStatus);
    })();
  };

  return (
    <Layout>
      <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[1400px] space-y-5">
          <div className="mb-1 text-center">
            <h2 className="flex items-center justify-center gap-3 text-2xl font-black text-slate-900">
              <ListChecks size={28} className="text-[#2f7d72]" />
              Controle de pedidos
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Acompanhe os pedidos por status e atualize o andamento de cada atendimento.
              Arraste os cartões para avançar no fluxo e use o ícone de olho para ver todos os detalhes.
            </p>
          </div>

          <form
            className="flex flex-col gap-4 bg-white rounded-2xl shadow p-4 md:p-5"
            onSubmit={handleFilterSubmit}
          >
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs mb-1 font-semibold text-gray-600">
                  Buscar cliente, telefone, item ou ID
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 focus:ring-2 ring-teal-200 outline-none"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Ex.: João, x-burger, 119999..."
                />
              </div>

              <div className="min-w-[170px]">
                <label className="block text-xs mb-1 font-semibold text-gray-600">Data início</label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 focus:ring-2 ring-teal-200 outline-none"
                  value={dateStart}
                  onChange={(event) => setDateStart(event.target.value)}
                  max={dateEnd}
                />
              </div>

              <div className="min-w-[170px]">
                <label className="block text-xs mb-1 font-semibold text-gray-600">Data fim</label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 focus:ring-2 ring-teal-200 outline-none"
                  value={dateEnd}
                  onChange={(event) => setDateEnd(event.target.value)}
                  min={dateStart}
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                  Filtrar
                </button>
                <button
                  type="button"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => fetchPedidos({ silent: true })}
                  disabled={isRefreshing || isLoading}
                >
                  {isRefreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                  Atualizar
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Colunas opcionais
              </span>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={columnPrefs.em_preparo}
                  onChange={(event) => setColumnPrefs((prev) => ({ ...prev, em_preparo: event.target.checked }))}
                  className="rounded border-gray-300 text-[#2f7d72] focus:ring-[#2f7d72]"
                />
                Em preparo
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={columnPrefs.em_transporte}
                  onChange={(event) => setColumnPrefs((prev) => ({ ...prev, em_transporte: event.target.checked }))}
                  className="rounded border-gray-300 text-[#2f7d72] focus:ring-[#2f7d72]"
                />
                Em transporte
              </label>
            </div>

            {integrationActive && (
              <p className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="relative inline-flex shrink-0 group">
                  <span
                    tabIndex={0}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-300 bg-white text-amber-700 cursor-help focus:outline-none focus:ring-2 focus:ring-amber-300"
                    aria-label="Informação sobre atualização em tempo real"
                  >
                    <Info size={13} aria-hidden="true" />
                  </span>
                  <span className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-72 rounded-lg border border-amber-200 bg-white px-3 py-2 text-left text-xs font-medium text-gray-700 shadow-lg group-hover:block group-focus-within:block">
                    Atualização em tempo real ativa: novos pedidos e mudanças de status entram automaticamente.
                  </span>
                </span>
                Integração ativa: atualização manual de status bloqueada. Os cards avançam automaticamente com os eventos da integração.
              </p>
            )}

            {feedbackError && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {feedbackError}
              </div>
            )}
          </form>

          {isLoading ? (
            <div className="text-center py-16 text-gray-400">Carregando pedidos...</div>
          ) : (
            <>
              <DragDropContext onDragEnd={handleDragEnd}>
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {visibleColumns.map((status) => {
                    const pedidosColuna = groupedPedidos.open[status];
                    const style = COLUMN_STYLES[status];

                    return (
                      <section
                        key={status}
                        className={`min-w-[310px] max-w-[360px] flex-1 rounded-2xl border ${style.border} bg-white shadow-sm p-3 md:p-4`}
                      >
                        <header className="mb-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {renderColumnIcon(status, style.iconClass)}
                            <h2 className="font-semibold text-gray-800">{STATUS_LABELS[status]}</h2>
                          </div>
                          <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            {pedidosColuna.length}
                          </span>
                        </header>

                        <Droppable droppableId={status}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`space-y-3 min-h-[40px] rounded-xl ${snapshot.isDraggingOver ? 'bg-gray-50/70' : ''}`}
                            >
                              {pedidosColuna.length === 0 ? (
                                <div className="text-sm text-gray-400 text-center py-8">Nenhum pedido</div>
                              ) : (
                                pedidosColuna.map((pedido, index) => {
                                  const pedidoId = String(pedido.id);
                                  const itens = getPedidoItems(pedido);
                                  const itensResumo = itens.slice(0, 3);
                                  const hiddenItemsCount = Math.max(itens.length - itensResumo.length, 0);
                                  const telefone = getClienteTelefone(pedido);
                                  const canUpdate = !integrationActive && (updatingPedidoId === null || updatingPedidoId === pedidoId);
                                  const statusOptions = getStatusOptionsForPedido(
                                    pedido.kanbanStatus,
                                    columnPrefs,
                                    { isRetirada: isRetiradaPedido(pedido) }
                                  );
                                  const dragDisabled = integrationActive || pedido.kanbanStatus === 'finalizado' || updatingPedidoId !== null;

                                  return (
                                    <Draggable
                                      key={pedidoId}
                                      draggableId={`pedido-${pedidoId}`}
                                      index={index}
                                      isDragDisabled={dragDisabled}
                                    >
                                      {(dragProvided, dragSnapshot) => (
                                        <div
                                          ref={dragProvided.innerRef}
                                          {...dragProvided.draggableProps}
                                          {...dragProvided.dragHandleProps}
                                        >
                                          <article
                                            className={`rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm ${dragSnapshot.isDragging ? 'shadow-lg border-gray-300' : ''}`}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="space-y-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 truncate">
                                                  {getClienteNome(pedido)}
                                                </p>
                                                {telefone && (
                                                  <p className="text-xs text-gray-500 truncate">{telefone}</p>
                                                )}
                                              </div>
                                              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_BADGE_CLASSES[pedido.kanbanStatus]}`}>
                                                {STATUS_LABELS[pedido.kanbanStatus]}
                                              </span>
                                            </div>

                                            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                                              <Calendar size={12} />
                                              <span>{formatDate(pedido.criado_em)}</span>
                                            </div>

                                            <ul className="mt-3 space-y-1.5">
                                              {itensResumo.map((item, itemIndex) => (
                                                <li key={`${pedidoId}-${itemIndex}`} className="text-sm text-gray-700">
                                                  <span className="font-semibold">{item.quantidade || 1}x</span>{' '}
                                                  {item.nome || 'Item sem nome'}
                                                </li>
                                              ))}
                                            </ul>
                                            {hiddenItemsCount > 0 && (
                                              <p className="mt-1 text-xs text-gray-500">+{hiddenItemsCount} item(ns)</p>
                                            )}

                                            <div className="mt-3 flex items-center gap-2">
                                              <select
                                                className={`flex-1 border rounded-lg px-2 py-1.5 bg-gray-50 text-sm focus:ring-2 ring-teal-200 outline-none ${integrationActive ? 'cursor-not-allowed opacity-70' : ''}`}
                                                value={pedido.kanbanStatus}
                                                onChange={(event) => handleStatusChange(pedido, event.target.value)}
                                                disabled={!canUpdate}
                                                title={integrationActive ? 'Atualização manual desabilitada com integração ativa.' : undefined}
                                              >
                                                {statusOptions.map((optionStatus) => (
                                                  <option key={`${pedidoId}-${optionStatus}`} value={optionStatus}>
                                                    {STATUS_LABELS[optionStatus]}
                                                  </option>
                                                ))}
                                              </select>

                                              <button
                                                type="button"
                                                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                                                onClick={() => setDetailPedidoId(pedidoId)}
                                                title="Ver pedido"
                                                aria-label="Ver pedido"
                                              >
                                                <Eye size={16} />
                                              </button>
                                            </div>
                                          </article>
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </section>
                    );
                  })}
                </div>
              </DragDropContext>
              <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
                <button
                  type="button"
                  onClick={() => setShowCancelados((prev) => !prev)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-2">
                    <XCircle size={16} className="text-rose-500" />
                    <h2 className="font-semibold text-gray-800">Pedidos cancelados</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold bg-rose-50 text-rose-700 px-2 py-1 rounded-full border border-rose-200">
                      {groupedPedidos.cancelados.length}
                    </span>
                    {showCancelados ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </button>

                {showCancelados && (
                  <div className="mt-4 space-y-3">
                    {groupedPedidos.cancelados.length === 0 ? (
                      <div className="text-sm text-gray-400 py-3">Nenhum pedido cancelado no período.</div>
                    ) : (
                      groupedPedidos.cancelados.map((pedido) => {
                        const pedidoId = String(pedido.id);
                        const itens = getPedidoItems(pedido);

                        return (
                          <article key={pedidoId} className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-rose-900">
                                  {getClienteNome(pedido)}
                                </p>
                                <p className="text-xs text-rose-700 mt-0.5">
                                  {formatDate(pedido.criado_em)}
                                </p>
                              </div>
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full border border-rose-200 text-rose-700 bg-white">
                                Cancelado
                              </span>
                            </div>
                            <p className="mt-2 text-sm text-rose-800">
                              {itens.map((item) => `${item.quantidade || 1}x ${item.nome || 'Item sem nome'}`).join(' • ')}
                            </p>
                            {pedido?.pedido?.cancelamento_motivo && (
                              <p className="mt-2 text-xs text-rose-700">
                                Motivo: {String(pedido.pedido.cancelamento_motivo)}
                              </p>
                            )}
                          </article>
                        );
                      })
                    )}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>

      {detailPedido && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-gray-100 p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-900">
                Pedido {getPedidoDisplayId(detailPedido) ? `#${getPedidoDisplayId(detailPedido)}` : ''}
              </h3>
              <button
                type="button"
                onClick={() => setDetailPedidoId(null)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                aria-label="Fechar detalhes do pedido"
              >
                <X size={16} />
              </button>
            </div>

            <PedidoDetailsContent pedido={detailPedido} />
          </div>
        </div>
      )}

      {cancelDialog.pedidoId && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100 p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-900">Cancelar pedido</h3>
              <button
                type="button"
                onClick={cancelDialog.confirming
                  ? () => setCancelDialog((current) => ({ ...current, confirming: false, error: '' }))
                  : closeCancelDialog}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                disabled={cancelDialog.submitting}
              >
                <XCircle size={16} />
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Informe o motivo que será enviado para o cliente no WhatsApp.
            </p>

            <div className="flex flex-wrap gap-2">
              {CANCEL_REASON_SUGGESTIONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setCancelDialog((current) => ({
                    ...current,
                    motivo: reason,
                    error: '',
                    confirming: false,
                  }))}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                  disabled={cancelDialog.submitting}
                >
                  {reason}
                </button>
              ))}
            </div>

            <textarea
              className="w-full min-h-[110px] border rounded-lg px-3 py-2 bg-gray-50 focus:ring-2 ring-teal-200 outline-none text-sm"
              value={cancelDialog.motivo}
              onChange={(event) => setCancelDialog((current) => ({
                ...current,
                motivo: event.target.value,
                error: '',
                confirming: false,
              }))}
              placeholder="Ex.: item indisponível no momento"
              disabled={cancelDialog.submitting}
            />

            {cancelDialog.confirming && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-semibold text-amber-900">
                  Este motivo sera enviado para o cliente no WhatsApp:
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-amber-900">
                  {cancelDialog.motivo.trim()}
                </p>
              </div>
            )}

            {cancelDialog.error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                {cancelDialog.error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCancelDialog}
                className="h-10 px-4 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                disabled={cancelDialog.submitting}
              >
                {cancelDialog.confirming ? 'Editar motivo' : 'Voltar'}
              </button>
              <button
                type="button"
                onClick={submitCancelamento}
                className="h-10 px-4 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={cancelDialog.submitting}
              >
                {cancelDialog.submitting ? <Loader2 size={15} className="animate-spin" /> : null}
                {cancelDialog.confirming ? 'Enviar e cancelar' : 'Continuar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Orders;

